import { useEffect, useRef, type CSSProperties } from "react";
import { useReducedMotion } from "motion/react";
import type { HomeCore } from "./home.types";

/**
 * The brand core (Figma 11 `Brand core`, 300×193): the Universe's lanes drawn as
 * tilted ellipses around the brand's pole. Each ring is its groups' arcs (a
 * group with no facts yet dashed), facts are 3px marks, facts new since the last
 * visit 5px in the accent, second filings hollow 4px squares. It assembles
 * from the pole outward on an arrival (M01) and breathes while an agent runs
 * (M07); both are CSS in home.css. Every ring's arcs and facts orbit the pole
 * along their ellipse, the inner rings faster (user call, 12 Oct, over the
 * still frame); reduced motion holds them still.
 */

const W = 300;
const H = 205;
const POLE = { x: 150, y: 90.5 };
/* The frame's five radii; perspective drifts each wider ring down and left. */
const RX_MIN = 33.5;
const RX_MAX = 145.8;
const TILT = 0.6;
const GAP = 0.05;
/* Seconds for one turn of the innermost ring; each ring out takes longer. */
const TURN_S = 70;
const TURN_STEP_S = 28;

function ellipseOf(i: number, count: number) {
  const step = (RX_MAX - RX_MIN) / Math.max(4, count - 1);
  const rx = RX_MIN + i * step;
  return {
    rx,
    ry: rx * TILT,
    cx: POLE.x - 0.055 * (rx - RX_MIN),
    cy: POLE.y + 0.093 * (rx - RX_MIN),
  };
}

function point(e: ReturnType<typeof ellipseOf>, a: number) {
  return { x: e.cx + e.rx * Math.cos(a), y: e.cy + e.ry * Math.sin(a) };
}

function arcPath(e: ReturnType<typeof ellipseOf>, start: number, end: number): string {
  const a0 = start + GAP;
  const a1 = Math.max(a0 + 0.01, end - GAP);
  const p0 = point(e, a0);
  const p1 = point(e, a1);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${e.rx.toFixed(2)} ${e.ry.toFixed(2)} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
}

/** `breathing`: an agent is running (M07): the rings breathe until it stops. */
export default function BrandCore({ core, initial, breathing = false }: { core: HomeCore | null; initial: string; breathing?: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const reduced = useReducedMotion();
  const rings = core?.rings ?? [];
  const ellipses = (rings.length ? rings : Array.from({ length: 4 }, (_, i) => ({ key: String(i), arcs: [] }))).map(
    (_, i, all) => ellipseOf(i, all.length),
  );

  // The orbit moves the marks straight on the DOM, so React draws them once
  // and a frame costs only their attributes.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || reduced || document.documentElement.dataset.coMotion === "reduce") return;
    const arcs = Array.from(svg.querySelectorAll<SVGPathElement>("path.home-core-arc"));
    const dots = Array.from(svg.querySelectorAll<SVGRectElement>("rect.home-core-dot"));
    if (arcs.length === 0 && dots.length === 0) return;
    const count = Number(svg.dataset.rings) || 1;
    const all = Array.from({ length: count }, (_, i) => ellipseOf(i, count));
    let frame = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const t = (now - t0) / 1000;
      const turn = (ring: number) => (t / (TURN_S + ring * TURN_STEP_S)) * Math.PI * 2;
      for (const el of arcs) {
        const e = all[Number(el.dataset.ring)];
        if (!e) continue;
        const d = turn(Number(el.dataset.ring));
        el.setAttribute("d", arcPath(e, Number(el.dataset.start) + d, Number(el.dataset.end) + d));
      }
      for (const el of dots) {
        const e = all[Number(el.dataset.ring)];
        if (!e) continue;
        const p = point(e, Number(el.dataset.angle) + turn(Number(el.dataset.ring)));
        const half = Number(el.getAttribute("width")) / 2;
        el.setAttribute("x", (p.x - half).toFixed(2));
        el.setAttribute("y", (p.y - half).toFixed(2));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [core, reduced]);

  return (
    <svg
      ref={svgRef}
      className="home-core"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      aria-hidden="true"
      data-rings={ellipses.length}
    >
      <g className="home-core-rings" data-breathing={breathing || undefined} style={{ transformOrigin: `${POLE.x}px ${POLE.y}px` }}>
      {ellipses.map((e, i) => {
        const ring = rings[i];
        if (!ring || ring.arcs.length === 0) {
          return (
            <ellipse key={i} cx={e.cx} cy={e.cy} rx={e.rx} ry={e.ry} className="home-core-arc home-core-arc--open" style={{ "--ring": i } as CSSProperties} />
          );
        }
        return ring.arcs.map((arc, j) => (
          <path
            key={`${ring.key}:${j}`}
            d={arcPath(e, arc.start, arc.end)}
            className={`home-core-arc${arc.charted ? "" : " home-core-arc--open"}`}
            style={{ "--ring": i } as CSSProperties}
            data-ring={i}
            data-start={arc.start}
            data-end={arc.end}
          />
        ));
      })}
      </g>
      {(core?.dots ?? []).map((dot) => {
        const e = ellipses[dot.ring];
        if (!e) return null;
        const p = point(e, dot.angle);
        const size = dot.kind === "new" ? 5 : dot.kind === "echo" ? 4 : 3;
        return (
          <rect
            key={dot.id}
            x={p.x - size / 2}
            y={p.y - size / 2}
            width={size}
            height={size}
            rx={dot.kind === "echo" ? 0 : 1.5}
            className={`home-core-dot home-core-dot--${dot.kind}`}
            style={{ "--ring": dot.ring } as CSSProperties}
            data-ring={dot.ring}
            data-angle={dot.angle}
          />
        );
      })}
      <circle cx={POLE.x} cy={POLE.y} r={14} className="home-core-pole" />
      <text x={POLE.x} y={POLE.y} className="home-core-initial" textAnchor="middle" dominantBaseline="central">
        {initial}
      </text>
    </svg>
  );
}

/** The core's skeleton: the frame's five rings and the pole, in the loading tone. */
export function BrandCoreSkeleton() {
  return (
    <svg className="home-core home-core--skeleton" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      {Array.from({ length: 5 }, (_, i) => {
        const e = ellipseOf(i, 5);
        return <ellipse key={i} cx={e.cx} cy={e.cy} rx={e.rx} ry={e.ry} />;
      })}
      <circle cx={POLE.x} cy={POLE.y} r={14} />
    </svg>
  );
}
